import { z } from 'zod/v3';
import { errorMap, zValidPeriod } from '../../../common/utils/schema';
import { isRangesOverlapping } from '../../utils/isRangesOverlapping';
import { UserViewPermissionValidFormDataSchema } from './UserViewPermissionValidFormDataSchema';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserValidFormDataSchema.translations.json';
import { UserRoleValidFormDataSchema } from './UserRoleValidFormDataSchema';
import { isOutsidePeriodRange } from '../../utils/isRangesOverlapping';

const { addTranslations, t } = i18nNsFactory('user-valid-form-data-schema');
addTranslations(translations);

export const UserValidFormDataSchema = z
  .object({
    username: z
      .string(errorMap(() => t('validation.username.required')))
      .min(1),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    nationalIdNumber: z
      .string(
        errorMap((issue) => {
          if (
            issue.code === z.ZodIssueCode.too_big ||
            issue.code === z.ZodIssueCode.too_small
          ) {
            return t('validation.nationalIdNumber.length');
          }
          if (issue.code === z.ZodIssueCode.invalid_string) {
            return t('validation.nationalIdNumber.numeric');
          }
        }),
      )
      .min(11)
      .max(11)
      .regex(/^\d*$/)
      .or(z.literal('').nullable()),
    validPeriod: zValidPeriod(),
    viewPermissions: UserViewPermissionValidFormDataSchema.array(),
    roles: UserRoleValidFormDataSchema.array(),
    isAdmin: z.boolean(),
    isSuperadmin: z.boolean(),
  })
  .superRefine(({ isSuperadmin, validPeriod, ...rest }) => ({
    isSuperadmin,
    validPeriod: isSuperadmin ? [null, null] : validPeriod,
    ...rest,
  }))
  .superRefine((data, ctx) => {
    const invalidIndexes = isOutsidePeriodRange(
      data.validPeriod,
      data.viewPermissions,
      'validPeriod',
    );
    invalidIndexes.forEach((index) => {
      ctx.addIssue({
        code: 'custom',
        message: t(
          'validation.viewPermissions.validPeriod.out-of-user-valid-period-range',
        ),
        path: ['viewPermissions', index, 'validPeriod'],
      });
    });
  })
  .superRefine((data, ctx) => {
    const invalidIndexes = isOutsidePeriodRange(
      data.validPeriod,
      data.roles,
      'validPeriod',
    );
    invalidIndexes.forEach((index) => {
      ctx.addIssue({
        code: 'custom',
        message: t(
          'validation.roles.validPeriod.out-of-user-valid-period-range',
        ),
        path: ['roles', index, 'validPeriod'],
      });
    });
  })
  .superRefine((data, ctx) => {
    const invalidIndexes = isRangesOverlapping(
      data.viewPermissions,
      'validPeriod',
    );
    invalidIndexes.forEach((index) => {
      ctx.addIssue({
        code: 'custom',
        message: t(
          'validation.viewPermissions.validPeriod.overlapping-with-other-view-permission',
        ),
        path: ['viewPermissions', index, 'validPeriod'],
      });
    });
  })
  .superRefine((data, ctx) => {
    const invalidIndexes = isRangesOverlapping(data.roles, 'validPeriod');
    invalidIndexes.forEach((index) => {
      ctx.addIssue({
        code: 'custom',
        message: t('validation.roles.validPeriod.overlapping-with-other-roles'),
        path: ['roles', index, 'validPeriod'],
      });
    });
  });
